import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PTOConfig } from '../../../../models/paid-time-off';
import {
  CompanyLocationConfigEx,
  ManualCheck,
  PayrollConfig,
  PayrollConfigSetupData,
  TaxHold,
  TimeclockModel
} from '../../../../models/payroll';
import { PayrollFrequency } from '../../../../models/payroll-info';
import { CompanyTaxCodeConfig, SchoolDistrict } from '../../../../models/tax-config';
import { parseDatePickerValue } from '../../../../utils/datepicker-value-parser';

@Component({
  selector: 'lib-payroll-config',
  templateUrl: './payroll-config.component.html',
  styleUrls: ['./payroll-config.component.css'],
  standalone: false
})
export class PayrollConfigComponent implements OnInit {
  constructor(private ngbModalService: NgbModal) {}

  @Input() locationConfig: CompanyLocationConfigEx;
  @Input() configSetupData: PayrollConfigSetupData;
  @Input() payrollConfig: PayrollConfig;
  @Input() timeclockFile: TimeclockModel;
  @Input() manualChecks: ManualCheck[];
  @Input() payDate: any; // should come in as formatted date time value
  @Input() ptoConfigs: PTOConfig[];
  @Input() stateLocalTaxes: Record<string, Record<string, Record<string, SchoolDistrict[]>>> = {};
  @Input() locked: boolean;

  @Output() saveChanges = new EventEmitter<PayrollConfig>();

  public incomeTaxes: CompanyTaxCodeConfig[] = [];
  public expandFrequencyOverride: boolean;
  public expandDeductionOverrides: boolean;
  public expandEarningOverrides: boolean;
  public expandTaxOverrides: boolean;
  public expandTimeOffOverrides: boolean;
  public heldLocalTaxes: {} = {};
  public config: PayrollConfig = new PayrollConfig();
  public overridePayFrequency: boolean;

  get payrollFrequencies() {
    return PayrollFrequency;
  }

  ngOnInit(): void {}

  // #region override select all checkbox logic
  public allEarningOverridesSelected() {
    return (
      this.configSetupData.Earnings.length == this.config.HoldEarnings.filter(x => x != null).length
    );
  }

  public allDeductionOverridesSelected() {
    return (
      this.configSetupData.Deductions.length ==
      this.config.HoldDeductions.filter(x => x != null).length
    );
  }

  public allTaxOverridesSelected() {
    return (
      this.configSetupData.WithholdingTaxes.length ==
      this.config.HoldTaxes.filter(x => x.Id != null && x.Id != undefined).length
    );
  }

  public allTimeOffOverridesSelected() {
    return this.ptoConfigs.length == this.config.HoldTimeOff.filter(x => x != null).length;
  }

  public anyEarningOverridesSelected() {
    return this.config.HoldEarnings.filter(x => x != null).length > 0;
  }

  public anyDeductionOverridesSelected() {
    return this.config.HoldDeductions.filter(x => x != null).length > 0;
  }

  public anyTaxOverridesSelected() {
    return this.config.HoldTaxes.filter(x => x.Id != null && x.Id != undefined).length > 0;
  }

  public anyTimeOffOverridesSelected() {
    return this.config.HoldTimeOff.filter(x => x != null).length > 0;
  }

  // bug 15438. see if any of the associated state wh checkboxes
  // are checked and have override percentages to toggle
  // disable logic on the local tax checkbox
  public disableLocalTax(stateAbbrev: string) {
    const heldStateTaxes = this._findHeldStateTax(stateAbbrev);

    // if at least one exists, but there is no override percentages set above 0
    // disable the local taxes checkbox. otherwise, enable
    return heldStateTaxes.length && !heldStateTaxes.find(x => x.OverridePercentage > 0);
  }

  public togglePayFrequencyOverride() {
    if (!this.overridePayFrequency) {
      this.config.PayrollFrequencyOverride = null;
    }
  }

  public toggleSelectAllEarnings(val) {
    this.config.HoldEarnings = this.config.HoldEarnings.filter(x => x != null);

    if (val.target.checked) {
      // add all earnings
      for (let earning of this.configSetupData.Earnings) {
        if (!this.config.HoldEarnings.find(x => x == earning.Id)) {
          this.config.HoldEarnings.push(earning.Id);
        }
      }
    } else {
      // remove all earnings
      this.config.HoldEarnings = [];
    }
  }

  public toggleSelectAllDeductions(val) {
    this.config.HoldDeductions = this.config.HoldDeductions.filter(x => x != null);

    if (val.target.checked) {
      // add all earnings
      for (let deduction of this.configSetupData.Deductions) {
        if (!this.config.HoldDeductions.find(x => x == deduction.Id)) {
          this.config.HoldDeductions.push(deduction.Id);
        }
      }
    } else {
      // remove all deductions
      this.config.HoldDeductions = [];
    }
  }

  public toggleSelectAllTaxes(val) {
    this.config.HoldTaxes = this.config.HoldTaxes.filter(x => x.Id != null);

    let index = 0;
    for (let tax of this.configSetupData.WithholdingTaxes) {
      if (!val.target.checked) {
        this.config.HoldTaxes[index] = new TaxHold();
      } else if (!this.config.HoldTaxes.find(x => x.Id == tax.TaxCode)) {
        // add all taxes
        this.config.HoldTaxes.push(new TaxHold(tax.TaxCode));
      }

      index++;
    }
  }

  public toggleSelectAllTimeOff(val) {
    if (val.target.checked) {
      // add all pto codes
      this.config.HoldTimeOff = this.ptoConfigs.map(t => t.Id);
    } else {
      // remove all pto codes
      this.config.HoldTimeOff = [];
    }
    this.config.HoldTimeOff = this.config.HoldTimeOff.filter(x => x != null);
  }

  // #endregion

  public onOverrideChange(collection, index, value) {
    if (collection[index]) {
      collection[index] = value;
    } else {
      collection[index] = null;
    }
  }

  public onTaxOverrideChange(
    index: number,
    tax: CompanyTaxCodeConfig,
    isLocalTax: boolean = false
  ) {
    if (this.config.HoldTaxes[index].Id) {
      this.config.HoldTaxes[index].Id = tax.TaxCode;
    } else {
      this.config.HoldTaxes[index] = new TaxHold();
    }

    if (isLocalTax || tax.StateAbbreviation == 'FED') {
      return;
    }

    // Bug 15438
    // when state wh tax is checked, automatically check the associated Local Tax checkbox
    const heldStateTaxes = this._findHeldStateTax(tax.StateAbbreviation);
    const localKey = 'Locals-' + tax.StateAbbreviation;
    const local = this.incomeTaxes.find(
      x => x.TaxType == 'Local' && x.StateAbbreviation == tax.StateAbbreviation
    );

    if (heldStateTaxes.length > 0 && !this.config.HoldTaxes[this.incomeTaxes.indexOf(local)]?.Id) {
      this.config.HoldTaxes[this.incomeTaxes.indexOf(local)] = new TaxHold();
      this.config.HoldTaxes[this.incomeTaxes.indexOf(local)].Id = localKey;
    }
  }

  private _setTaxList() {
    this.incomeTaxes = this.configSetupData.WithholdingTaxes.filter(
      x => x.TaxType == 'I' && x.IsActive && x.DisplayName
    );

    // add a single 'local tax' checkbox for any states that have them
    const states = [
      ...new Set(
        this.incomeTaxes.filter(x => x.StateAbbreviation != 'FED').map(x => x.StateAbbreviation)
      )
    ];

    states.forEach(state => {
      if (this.stateLocalTaxes[state]) {
        const taxCode = 'Locals-' + state;
        const config = new CompanyTaxCodeConfig(
          taxCode,
          'Local',
          `${state} Local Taxes`,
          '',
          [],
          [],
          false,
          0,
          0,
          true,
          false,
          state
        );

        this.incomeTaxes.push(config);
      }
    });

    this.incomeTaxes.sort((a, b) => {
      if (a.StateAbbreviation == b.StateAbbreviation) {
        return a.TaxType == 'Local' ? 1 : -1;
      }

      if (a.StateAbbreviation == 'FED') {
        return -1;
      }

      if (b.StateAbbreviation == 'FED') {
        return 1;
      }

      return a.StateAbbreviation > b.StateAbbreviation ? 1 : -1;
    });
  }

  public openPayrollConfig(content) {
    this._setTaxList();

    this.configSetupData.Earnings = this.configSetupData.Earnings.filter(x => x.IsActive);
    this.configSetupData.Deductions = this.configSetupData.Deductions.filter(x => x.IsActive);

    this.config = JSON.parse(JSON.stringify(this.payrollConfig));
    this._initializeTaxHolds();
    this.ngbModalService
      .open(content, {
        backdrop: 'static',
        keyboard: false,
        size: 'lg',
        windowClass: 'centered'
      })
      .result.then(
        result => {
          // modal save button pressed
          const c = JSON.parse(JSON.stringify(this.config));
          this.saveChanges.emit(c);
        },
        dismiss => {
          // modal dismissed. no changes saved.
        }
      );
  }

  // in order to model bind easier from the template,
  // instantiate HoldTaxes as an array of empty TaxHold objects
  private _initializeTaxHolds() {
    for (let tax of this.configSetupData.WithholdingTaxes) {
      if (!this.config.HoldTaxes.find(x => x.Id == tax.TaxCode)) {
        this.config.HoldTaxes.push(new TaxHold());
      }
    }
  }

  private _findHeldStateTax(stateAbbrev: string) {
    // find all state wh tax codes for this abbreviation
    const taxCodes = this.configSetupData.WithholdingTaxes.filter(
      x => x.StateAbbreviation == stateAbbrev
    ).map(x => x.TaxCode);

    // see if any of those tax codes exist in the HoldTaxes collection
    const heldStateTaxes = [];
    taxCodes.forEach(code => {
      const match = this.config.HoldTaxes.find(taxHold => taxHold.Id == code);
      if (match) {
        heldStateTaxes.push(match);
      }
    });

    return heldStateTaxes;
  }
}
